import { motion } from 'framer-motion';
import { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useGlobalPlayer, type GlobalTrack } from '../../hooks/useGlobalPlayer';
import { useAuth } from '../../hooks/useAuth';
import { CheckInRankBadge, getRank, RANK_CONFIG } from './components/CheckInRankBadge';

interface ShopData {
  id: string;
  shop_name: string;
  thumbnail: string | null;
  color: string | null;
  description: string | null;
  address: string | null;
  phone: string | null;
  business_hours: string | null;
}

interface ProfileData {
  nfc_display_mode: string;
  nfc_curation_genres: string[];
  nfc_welcome_message: string;
  nfc_thank_you_message: string;
  nfc_pattern_id: string | null;
  nfc_custom_config: Record<string, any>;
}

interface NFCPattern {
  id: string;
  name: string;
  type: 'text' | 'image' | 'coupon';
  default_config: Record<string, any>;
}

interface EventData {
  id: string;
  title: string;
  time: string;
  event_date: string;
  category: string | null;
  thumbnail: string | null;
}

// 🎟️ DEEP DIVERクーポンン状態
interface DeepDiverCoupon {
  discount: string;
  expires_at: string;
  period_label?: string;
  claimed: boolean;
  used: boolean;
}

export default function NFCPortalPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const nodeId = searchParams.get('node_id');
  const shareId = searchParams.get('share_id'); // シェア経由流入の識別子
  const { setTrack, play } = useGlobalPlayer();
  const { user } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [shopData, setShopData] = useState<ShopData | null>(null);
  const [profileData, setProfileData] = useState<ProfileData | null>(null);
  const [nfcPattern, setNfcPattern] = useState<NFCPattern | null>(null);
  const [events, setEvents] = useState<EventData[]>([]);
  const [nowPlaying, setNowPlaying] = useState<{
    title: string;
    artist: string;
    duration: string;
  } | null>(null);
  const [visitCount, setVisitCount] = useState<number | null>(null);
  const [sharedPlatform, setSharedPlatform] = useState<string | null>(null);
  const [showThankYou, setShowThankYou] = useState(false);
  const [copiedCoupon, setCopiedCoupon] = useState(false);

  // 🎟️ DEEP DIVERクーポン状態
  const [deepDiverCoupon, setDeepDiverCoupon] = useState<DeepDiverCoupon | null>(null);
  const [deepDiverMessage, setDeepDiverMessage] = useState<string | null>(null);
  const [showCouponStaffView, setShowCouponStaffView] = useState(false);

  // 重複記録を防ぐためのフラグ
  const hasRecorded = useRef(false);
  const hasRecordedVisit = useRef(false); // シェア流入記録フラグ
  const thankYouTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ─── node_idがない場合：既存のGPSリダイレクト処理 ───
  useEffect(() => {
    if (!nodeId) {
      handleGPSRedirect();
      return;
    }

    fetchNodeData();
    // シェア経由流入を記録
    if (shareId && !hasRecordedVisit.current) {
      hasRecordedVisit.current = true;
      recordShareVisit();
    }
  }, [nodeId]);

  // ─── シェアトラッキング ───
  const trackShare = async (platform: string) => {
    let newShareId: string | null = null;
    try {
      const { data } = await supabase
        .from('nfc_shares')
        .insert({
          node_id: nodeId,
          user_id: user?.id ?? null,
          platform,
        })
        .select('id')
        .maybeSingle();
      newShareId = data?.id ?? null;
    } catch (err) {
      console.error('Share tracking error:', err);
    }

    // お礼メッセージを表示
    setSharedPlatform(platform);
    setShowThankYou(true);
    if (thankYouTimer.current) clearTimeout(thankYouTimer.current);
    thankYouTimer.current = setTimeout(() => {
      setShowThankYou(false);
      setSharedPlatform(null);
    }, 4000);

    return newShareId;
  };

  // ─── シェア流入記録 ───
  const recordShareVisit = async () => {
    if (!shareId || !nodeId) return;
    try {
      await supabase
        .from('nfc_share_visits')
        .insert({
          share_id: shareId,
          node_id: nodeId,
          visitor_user_id: user?.id ?? null,
          visited_at: new Date().toISOString(),
        });
    } catch (err) {
      console.error('recordShareVisit error:', err);
    }
  };

  // ─── 来場記録 ───
  const recordVisit = async (userId: string, targetNodeId: string) => {
    try {
      await supabase.from('dive_logs').insert({
        user_id: userId,
        target_id: targetNodeId,
        action_type: 'nfc_crash',
        created_at: new Date().toISOString(),
      });
    } catch (err) {
      console.error('recordVisit error:', err);
    }
  };

  const handleGPSRedirect = async () => {
    try {
      setLoading(true);

      // 位置情報を取得
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0,
        });
      });

      const { latitude, longitude } = position.coords;

      // 近くのアーティストを取得
      const { data: artists, error: artistError } = await supabase
        .from('artist_layers')
        .select('id, artist_name, prefecture, latitude, longitude')
        .not('latitude', 'is', null)
        .not('longitude', 'is', null);

      if (artistError) throw artistError;

      if (!artists || artists.length === 0) {
        setError('近くにアーティストが見つかりませんでした');
        setLoading(false);
        return;
      }

      // 距離を計算してソート
      const artistsWithDistance = artists.map((artist) => {
        const distance = calculateDistance(
          latitude,
          longitude,
          artist.latitude!,
          artist.longitude!
        );
        return { ...artist, distance };
      });

      artistsWithDistance.sort((a, b) => a.distance - b.distance);

      // 最も近いアーティストにリダイレクト
      const nearestArtist = artistsWithDistance[0];
      navigate(`/artist/${nearestArtist.id}`);
    } catch (err) {
      console.error('GPS redirect error:', err);
      // 位置情報取得失敗時のフォールバック
      await handleGPSFallback();
    }
  };

  // ─── GPS失敗時のフォールバック ───
  const handleGPSFallback = async () => {
    try {
      // ログイン中のユーザーを確認
      const { data: { user } } = await supabase.auth.getUser();

      if (user) {
        // ログイン済み：登録都道府県を取得
        const { data: profile } = await supabase
          .from('profiles')
          .select('prefecture')
          .eq('id', user.id)
          .maybeSingle();

        if (profile?.prefecture) {
          // 都道府県が登録されている場合はその都道府県ページへ
          navigate(`/prefecture/${encodeURIComponent(profile.prefecture)}`);
          return;
        }
      }

      // 未ログイン or 都道府県未設定：全国ディレクトリへ
      navigate('/directory');
    } catch (err) {
      console.error('Fallback error:', err);
      navigate('/directory');
    }
  };

  const calculateDistance = (lat1: number, lon1: number, lat2: number, lon2: number): number => {
    const R = 6371; // 地球の半径（km）
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  };

  // ─── node_idがある場合：店舗ポータルのデータ取得 ───
  const fetchNodeData = async () => {
    try {
      setLoading(true);
      setError(null);

      // ─── 店舗情報を取得 ───
      const { data: shopLayer, error: shopError } = await supabase
        .from('shop_layers')
        .select('user_id, shop_name, thumbnail, color, description, address, phone, business_hours')
        .eq('user_id', nodeId)
        .maybeSingle();

      if (shopError) throw shopError;
      if (!shopLayer) {
        setError('店舗が見つかりません');
        setLoading(false);
        return;
      }

      setShopData({
        id: shopLayer.user_id,
        shop_name: shopLayer.shop_name,
        thumbnail: shopLayer.thumbnail,
        color: shopLayer.color,
        description: shopLayer.description,
        address: shopLayer.address,
        phone: shopLayer.phone,
        business_hours: shopLayer.business_hours,
      });

      // ─── 来場記録（ログインユーザーのみ・1回のみ） ───
      if (user && nodeId && !hasRecorded.current) {
        hasRecorded.current = true;
        await recordVisit(user.id, nodeId);
        // 来場記録後にチェックイン回数を取得
        await fetchVisitCount(user.id, nodeId);
      } else if (user && nodeId) {
        // 既に記録済みの場合もカウントを取得
        await fetchVisitCount(user.id, nodeId);
      }

      // ─── プロフィール情報（NFC設定 + パターン設定）を取得 ───
      const { data: profile, error: profileError } = await supabase
        .from('profiles')
        .select('nfc_display_mode, nfc_curation_genres, nfc_welcome_message, nfc_thank_you_message, nfc_pattern_id, nfc_custom_config')
        .eq('id', nodeId)
        .maybeSingle();

      if (profileError) throw profileError;

      const displayMode = profile?.nfc_display_mode || 'shop';
      const curationGenres = profile?.nfc_curation_genres || [];
      const welcomeMessage = profile?.nfc_welcome_message || '';
      const thankYouMessage = profile?.nfc_thank_you_message || '';
      const patternId = profile?.nfc_pattern_id || null;
      const customConfig = profile?.nfc_custom_config || {};

      setProfileData({
        nfc_display_mode: displayMode,
        nfc_curation_genres: curationGenres,
        nfc_welcome_message: welcomeMessage,
        nfc_thank_you_message: thankYouMessage,
        nfc_pattern_id: patternId,
        nfc_custom_config: customConfig,
      });

      // 🎟️ DEEP DIVER限定コンテンツの処理
      if (user && visitCount !== null && visitCount >= 10 && customConfig.deep_diver_content) {
        const ddContent = customConfig.deep_diver_content;
        
        // 限定メッセージ
        if (ddContent.message) {
          setDeepDiverMessage(ddContent.message);
        }

        // 限定クーポン
        if (ddContent.coupon) {
          const coupon = ddContent.coupon;
          const expiresAt = new Date(coupon.expires_at);
          const now = new Date();

          // (1) 有効期限チェック
          if (expiresAt > now) {
            // (2) クーポン期間キー生成（有効期限の日付文字列）
            const periodKey = coupon.expires_at.split('T')[0]; // YYYY-MM-DD

            // (3) 既にクレーム済みかチェック
            const { data: existingClaim, error: claimCheckError } = await supabase
              .from('nfc_deep_diver_coupon_claims')
              .select('id, used_at')
              .eq('node_id', nodeId)
              .eq('user_id', user.id)
              .eq('coupon_period_key', periodKey)
              .maybeSingle();

            if (claimCheckError && claimCheckError.code !== 'PGRST116') {
              console.error('Coupon claim check error:', claimCheckError);
            }

            if (existingClaim) {
              // 既にクレーム済み
              setDeepDiverCoupon({
                discount: coupon.discount,
                expires_at: coupon.expires_at,
                period_label: coupon.period_label,
                claimed: true,
                used: !!existingClaim.used_at,
              });
            } else {
              // (4) 未クレーム → INSERT
              const { error: insertError } = await supabase
                .from('nfc_deep_diver_coupon_claims')
                .insert({
                  node_id: nodeId,
                  user_id: user.id,
                  coupon_period_key: periodKey,
                });

              if (insertError) {
                console.error('Coupon claim insert error:', insertError);
              } else {
                // クーポン表示
                setDeepDiverCoupon({
                  discount: coupon.discount,
                  expires_at: coupon.expires_at,
                  period_label: coupon.period_label,
                  claimed: true,
                  used: false,
                });
              }
            }
          }
        }
      }

      // ─── NFCパターン情報を取得 ───
      if (patternId) {
        const { data: patternData, error: patternError } = await supabase
          .from('nfc_interaction_patterns')
          .select('id, name, type, default_config')
          .eq('id', patternId)
          .maybeSingle();

        if (!patternError && patternData) {
          setNfcPattern(patternData);
        }
      }

      // ─── 今日のイベントを取得 ───
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const tomorrow = new Date(today);
      tomorrow.setDate(today.getDate() + 1);

      const { data: eventsData, error: eventsError } = await supabase
        .from('events')
        .select('id, title, time, event_date, category, thumbnail')
        .eq('shop_id', nodeId)
        .gte('event_date', today.toISOString())
        .lt('event_date', tomorrow.toISOString())
        .order('event_date', { ascending: true })
        .limit(2);

      if (eventsError) throw eventsError;
      setEvents(eventsData || []);

      // ─── バックグラウンド再生用の音楽を取得して再生 ───
      await fetchAndPlayMusic(shopLayer.shop_name, curationGenres);

      setLoading(false);
    } catch (err) {
      console.error('Error fetching node data:', err);
      setError('データの取得に失敗しました');
      setLoading(false);
    }
  };

  // ─── チェックイン回数を取得 ───
  const fetchVisitCount = async (userId: string, targetNodeId: string) => {
    try {
      const { data, error } = await supabase
        .from('dive_logs')
        .select('id')
        .eq('user_id', userId)
        .eq('target_id', targetNodeId)
        .eq('action_type', 'nfc_crash');

      if (error) throw error;
      setVisitCount(data?.length ?? 0);
    } catch (err) {
      console.error('Error fetching visit count:', err);
      setVisitCount(0);
    }
  };

  // ─── 音楽再生ロジック ───
  const fetchAndPlayMusic = async (shopName: string, curationGenres: string[]) => {
    try {
      let artistIds: string[] = [];

      // キュレーションジャンルがある場合はフィルタリング
      if (curationGenres.length > 0) {
        const { data: artistsWithGenres, error: genreError } = await supabase
          .from('artist_main_genres')
          .select('artist_id')
          .in('genre_name', curationGenres);

        if (!genreError && artistsWithGenres && artistsWithGenres.length > 0) {
          artistIds = artistsWithGenres.map((a) => a.artist_id);
        }
      }

      // アーティストを取得
      let query = supabase
        .from('artist_layers')
        .select('user_id, artist_name, color, sample_track_url, sample_track_name, embed_type')
        .not('sample_track_url', 'is', null)
        .limit(20);

      if (artistIds.length > 0) {
        query = query.in('user_id', artistIds);
      }

      const { data: artists, error: artistError } = await query;

      if (artistError) throw artistError;

      if (artists && artists.length > 0) {
        // ランダムに1曲選択
        const randomArtist = artists[Math.floor(Math.random() * artists.length)];

        const trackName = randomArtist.sample_track_name || 'Node Exclusive Mix';
        const artistName = randomArtist.artist_name;

        // GlobalPlayerにセット
        const track: GlobalTrack = {
          url: randomArtist.sample_track_url || '',
          trackName,
          artistName,
          artistId: randomArtist.user_id,
          artistType: 'artist',
          accentColor: randomArtist.color || '#22d3ee',
          embedType: (randomArtist.embed_type as GlobalTrack['embedType']) || 'link',
        };

        setNowPlaying({
          title: trackName,
          artist: artistName,
          duration: '4:32',
        });

        setTrack(track);
        play();
      } else {
        // フォールバック：モックデータ
        const portalTrack: GlobalTrack = {
          url: '',
          trackName: 'Node Exclusive Mix',
          artistName: shopName + ' Curation',
          artistId: nodeId || '',
          artistType: 'mock',
          accentColor: '#00ff41',
          embedType: 'none',
        };

        setNowPlaying({
          title: portalTrack.trackName,
          artist: portalTrack.artistName,
          duration: '4:32',
        });

        setTrack(portalTrack);
        play();
      }
    } catch (err) {
      console.error('Error fetching music:', err);
      // エラー時もフォールバック
      const portalTrack: GlobalTrack = {
        url: '',
        trackName: 'Node Exclusive Mix',
        artistName: shopName + ' Curation',
        artistId: nodeId || '',
        artistType: 'mock',
        accentColor: '#00ff41',
        embedType: 'none',
      };

      setNowPlaying({
        title: portalTrack.trackName,
        artist: portalTrack.artistName,
        duration: '4:32',
      });

      setTrack(portalTrack);
      play();
    }
  };

  // ─── クーポンコピー処理 ───
  const handleCopyCoupon = async (couponText: string) => {
    try {
      await navigator.clipboard.writeText(couponText);
      setCopiedCoupon(true);
      setTimeout(() => setCopiedCoupon(false), 2000);
    } catch (err) {
      console.error('Failed to copy coupon:', err);
    }
  };

  // 🎟️ 店員用：クーポン使用済み処理
  const handleMarkCouponAsUsed = async () => {
    if (!user || !nodeId || !deepDiverCoupon) return;

    const confirmed = window.confirm('このクーポンを使用済みにしますか？\n\n※この操作は取り消せません。');
    if (!confirmed) return;

    try {
      const periodKey = deepDiverCoupon.expires_at.split('T')[0];

      // クーポンクレームレコードを更新
      const { error: updateError } = await supabase
        .from('nfc_deep_diver_coupon_claims')
        .update({
          used_at: new Date().toISOString(),
          used_by_staff_id: user.id,
        })
        .eq('node_id', nodeId)
        .eq('coupon_period_key', periodKey);

      if (updateError) throw updateError;

      // 監査ログに記録
      await supabase.from('audit_logs').insert({
        action: 'NFC_COUPON_USED',
        user_id: user.id,
        details: {
          node_id: nodeId,
          coupon_period_key: periodKey,
          discount: deepDiverCoupon.discount,
          used_at: new Date().toISOString(),
        },
      });

      // 画面上のクーポン状態を更新
      setDeepDiverCoupon({
        ...deepDiverCoupon,
        used: true,
      });

      alert('✅ クーポンを使用済みにしました');
    } catch (err) {
      console.error('Error marking coupon as used:', err);
      alert('❌ クーポンの使用済み処理に失敗しました');
    }
  };

  // ─── 演出パターンの設定値を取得 ───
  const getPatternConfig = () => {
    if (!nfcPattern) return null;
    
    // カスタム設定がある場合はそれを優先、なければデフォルト設定を使用
    const config = Object.keys(profileData?.nfc_custom_config || {}).length > 0
      ? profileData?.nfc_custom_config
      : nfcPattern.default_config;

    return {
      type: nfcPattern.type,
      config,
    };
  };

  const patternConfig = getPatternConfig();

  // ─── ローディング状態 ───
  if (loading) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <motion.div
            className="w-16 h-16 rounded-full border-4 border-cyan-400/20 border-t-cyan-400"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
          />
          <p className="text-white/50 text-sm tracking-widest">LOADING...</p>
        </div>
      </div>
    );
  }

  // ─── エラー状態 ───
  if (error || (!nodeId && !loading)) {
    return (
      <div className="bg-black min-h-screen text-white flex items-center justify-center px-6">
        <div className="text-center max-w-md">
          <i className="ri-error-warning-line text-6xl text-red-400/60 mb-4" />
          <h2 className="text-xl font-bold tracking-wide mb-2">エラー</h2>
          <p className="text-white/50 text-sm">{error || '店舗情報が見つかりません'}</p>
        </div>
      </div>
    );
  }

  if (!shopData) return null;

  const displayMode = profileData?.nfc_display_mode || 'shop';

  // ─── SNSシェア用（share_idをURLに付与） ───
  const buildShareUrl = (sid: string | null) =>
    `https://dignia.app/nfc?node_id=${shopData.id}${sid ? `&share_id=${sid}` : ''}`;

  const shareText = `今日は「${shopData.shop_name}」に来ています！🎵 #DIGNIA #${shopData.shop_name.replace(/\s/g, '')}`;

  // share_idなしの基本URL（シェア前に使用）
  const shareUrl = `https://dignia.app/nfc?node_id=${shopData.id}`;
  const xShareUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`;
  const lineShareUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareText)}`;
  const instagramStoryHint = true;

  // ─── シェアボタンのクリックハンドラ（share_id付きURLを生成） ───
  const handleXShare = async () => {
    const sid = await trackShare('x');
    const url = buildShareUrl(sid);
    const tweetUrl = `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(url)}`;
    window.open(tweetUrl, '_blank', 'noopener,noreferrer');
  };

  const handleLineShare = async () => {
    const sid = await trackShare('line');
    const url = buildShareUrl(sid);
    const lineUrl = `https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(url)}&text=${encodeURIComponent(shareText)}`;
    window.open(lineUrl, '_blank', 'noopener,noreferrer');
  };

  const handleInstagramShare = async () => {
    const sid = await trackShare('instagram');
    const url = buildShareUrl(sid);
    navigator.clipboard?.writeText(`${shareText}\n${url}`).catch(() => {});
  };

  // ノードオーナーかどうかを判定
  const isNodeOwner = user?.id === nodeId;

  return (
    <div className="bg-black min-h-screen text-white relative overflow-hidden flex flex-col">

      {/* ── シェアお礼トースト ── */}
      <motion.div
        className="fixed top-6 left-1/2 z-50 pointer-events-none"
        style={{ x: '-50%' }}
        initial={{ opacity: 0, y: -30, scale: 0.9 }}
        animate={showThankYou ? { opacity: 1, y: 0, scale: 1 } : { opacity: 0, y: -30, scale: 0.9 }}
        transition={{ duration: 0.4, ease: 'easeOut' }}
      >
        <div
          className="flex items-center gap-golden-sm px-golden-md py-golden-sm rounded-golden-lg backdrop-blur-md"
          style={{
            background: 'linear-gradient(135deg, rgba(34,211,238,0.25), rgba(168,85,247,0.2))',
            border: '1px solid rgba(34,211,238,0.4)',
            boxShadow: '0 0 30px rgba(34,211,238,0.3), 0 8px 32px rgba(0,0,0,0.6)',
          }}
        >
          <motion.div
            animate={showThankYou ? { rotate: [0, -15, 15, -10, 10, 0], scale: [1, 1.3, 1] } : {}}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="w-6 h-6 flex items-center justify-center"
          >
            <span className="text-xl">🎉</span>
          </motion.div>
          <div>
            <p className="text-white text-sm font-bold tracking-wide whitespace-nowrap">
              シェアありがとう！
            </p>
            <p className="text-cyan-400/70 text-[10px] tracking-wider whitespace-nowrap">
              {sharedPlatform === 'x' && 'X (Twitter) でシェアしました'}
              {sharedPlatform === 'line' && 'LINE でシェアしました'}
              {sharedPlatform === 'instagram' && 'テキストをコピーしました'}
              {sharedPlatform === 'url' && 'URLをコピーしました'}
            </p>
          </div>
          <motion.div
            animate={showThankYou ? { scale: [0, 1.2, 1] } : {}}
            transition={{ duration: 0.4, delay: 0.2 }}
            className="w-5 h-5 flex items-center justify-center"
          >
            <i className="ri-check-line text-cyan-400 text-base" />
          </motion.div>
        </div>
      </motion.div>

      {/* ── 背景アニメーション（波紋効果を強化） ── */}
      <div className="absolute inset-0 pointer-events-none overflow-hidden">
        {/* ランク別背景カラー */}
        {user && visitCount !== null && (() => {
          const rank = getRank(visitCount);
          if (rank === 'deep_diver') return (
            <>
              {/* 呼吸するオーブ1 */}
              <motion.div
                className="absolute w-[700px] h-[700px] rounded-full blur-[120px]"
                style={{ top: '-15%', left: '-10%', background: 'rgba(34,211,238,0.25)' }}
                animate={{ 
                  x: [0, 60, -30, 0], 
                  y: [0, 40, -20, 0],
                  scale: [1, 1.15, 0.95, 1],
                }}
                transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
              />
              {/* 呼吸するオーブ2 */}
              <motion.div
                className="absolute w-[600px] h-[600px] rounded-full blur-[120px]"
                style={{ bottom: '-10%', right: '-10%', background: 'rgba(168,85,247,0.25)' }}
                animate={{ 
                  x: [0, -50, 30, 0], 
                  y: [0, -40, 20, 0],
                  scale: [1, 1.2, 0.9, 1],
                }}
                transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
              />
              {/* 中央パルス波紋 */}
              <motion.div
                className="absolute w-[500px] h-[500px] rounded-full blur-[100px]"
                style={{ top: '30%', left: '30%', background: 'rgba(34,211,238,0.12)' }}
                animate={{ 
                  scale: [1, 1.6, 1], 
                  opacity: [0.3, 0.8, 0.3],
                }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeInOut' }}
              />
              {/* 波紋リング1 */}
              <motion.div
                className="absolute w-[400px] h-[400px] rounded-full"
                style={{ 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  border: '2px solid rgba(34,211,238,0.15)',
                }}
                animate={{ 
                  scale: [0.8, 2.5, 0.8], 
                  opacity: [0.6, 0, 0.6],
                }}
                transition={{ duration: 8, repeat: Infinity, ease: 'easeOut' }}
              />
              {/* 波紋リング2 */}
              <motion.div
                className="absolute w-[350px] h-[350px] rounded-full"
                style={{ 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  border: '2px solid rgba(168,85,247,0.12)',
                }}
                animate={{ 
                  scale: [1, 2.8, 1], 
                  opacity: [0.5, 0, 0.5],
                }}
                transition={{ duration: 10, repeat: Infinity, ease: 'easeOut', delay: 1 }}
              />
            </>
          );
          if (rank === 'regular') return (
            <>
              <motion.div
                className="absolute w-[700px] h-[700px] rounded-full blur-[120px]"
                style={{ top: '-15%', left: '-10%', background: 'rgba(251,146,60,0.18)' }}
                animate={{ 
                  x: [0, 60, -30, 0], 
                  y: [0, 40, -20, 0],
                  scale: [1, 1.12, 0.98, 1],
                }}
                transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="absolute w-[600px] h-[600px] rounded-full blur-[120px]"
                style={{ bottom: '-10%', right: '-10%', background: 'rgba(234,88,12,0.15)' }}
                animate={{ 
                  x: [0, -50, 30, 0], 
                  y: [0, -40, 20, 0],
                  scale: [1, 1.18, 0.92, 1],
                }}
                transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
              />
              {/* 波紋リング */}
              <motion.div
                className="absolute w-[350px] h-[350px] rounded-full"
                style={{ 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  border: '2px solid rgba(251,146,60,0.2)',
                }}
                animate={{ 
                  scale: [0.9, 2.2, 0.9], 
                  opacity: [0.5, 0, 0.5],
                }}
                transition={{ duration: 7, repeat: Infinity, ease: 'easeOut' }}
              />
            </>
          );
          // first visit
          return (
            <>
              <motion.div
                className="absolute w-[700px] h-[700px] rounded-full bg-cyan-500/20 blur-[120px]"
                style={{ top: '-15%', left: '-10%' }}
                animate={{ 
                  x: [0, 60, -30, 0], 
                  y: [0, 40, -20, 0],
                  scale: [1, 1.1, 0.95, 1],
                }}
                transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
              />
              <motion.div
                className="absolute w-[600px] h-[600px] rounded-full bg-purple-600/20 blur-[120px]"
                style={{ bottom: '-10%', right: '-10%' }}
                animate={{ 
                  x: [0, -50, 30, 0], 
                  y: [0, -40, 20, 0],
                  scale: [1, 1.15, 0.9, 1],
                }}
                transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
              />
              {/* 波紋リング */}
              <motion.div
                className="absolute w-[300px] h-[300px] rounded-full"
                style={{ 
                  top: '50%', 
                  left: '50%', 
                  transform: 'translate(-50%, -50%)',
                  border: '2px solid rgba(34,211,238,0.25)',
                }}
                animate={{ 
                  scale: [1, 2, 1], 
                  opacity: [0.4, 0, 0.4],
                }}
                transition={{ duration: 6, repeat: Infinity, ease: 'easeOut' }}
              />
            </>
          );
        })()}
        {/* ログイン前 or visitCount未取得時はデフォルト背景 */}
        {(!user || visitCount === null) && (
          <>
            <motion.div
              className="absolute w-[700px] h-[700px] rounded-full bg-cyan-500/20 blur-[120px]"
              style={{ top: '-15%', left: '-10%' }}
              animate={{ 
                x: [0, 60, -30, 0], 
                y: [0, 40, -20, 0],
                scale: [1, 1.1, 0.95, 1],
              }}
              transition={{ duration: 18, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute w-[600px] h-[600px] rounded-full bg-purple-600/20 blur-[120px]"
              style={{ bottom: '-10%', right: '-10%' }}
              animate={{ 
                x: [0, -50, 30, 0], 
                y: [0, -40, 20, 0],
                scale: [1, 1.15, 0.9, 1],
              }}
              transition={{ duration: 22, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.div
              className="absolute w-[400px] h-[400px] rounded-full bg-cyan-400/10 blur-[100px]"
              style={{ top: '40%', left: '50%', transform: 'translate(-50%, -50%)' }}
              animate={{ scale: [1, 1.3, 1], opacity: [0.4, 0.8, 0.4] }}
              transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
            />
            {/* 波紋リング */}
            <motion.div
              className="absolute w-[300px] h-[300px] rounded-full"
              style={{ 
                top: '50%', 
                left: '50%', 
                transform: 'translate(-50%, -50%)',
                border: '2px solid rgba(34,211,238,0.2)',
              }}
              animate={{ 
                scale: [1, 2, 1], 
                opacity: [0.4, 0, 0.4],
              }}
              transition={{ duration: 6, repeat: Infinity, ease: 'easeOut' }}
            />
          </>
        )}
      </div>

      {/* ── ヘッダー ── */}
      <motion.header
        className="relative z-10 flex flex-col items-center pt-12 pb-6 px-golden-md"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        <p className="text-cyan-400/60 text-[10px] tracking-[0.4em] mb-2 font-light">
          NFC PORTAL
        </p>
        <h1 className="text-white text-2xl font-bold tracking-widest text-center">
          WELCOME TO
        </h1>
        <h2
          className="text-3xl font-black tracking-widest text-center mt-1"
          style={{
            background: 'linear-gradient(90deg, #22d3ee, #a855f7, #22d3ee)',
            backgroundSize: '200% auto',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
          }}
        >
          {shopData.shop_name}
        </h2>

        {/* 区切り線 */}
        <div className="mt-golden-md flex items-center gap-golden-sm w-full max-w-xs">
          <div className="flex-1 h-px bg-gradient-to-r from-transparent to-cyan-500/40" />
          <div className="w-1.5 h-1.5 rounded-full bg-cyan-400" />
          <div className="flex-1 h-px bg-gradient-to-l from-transparent to-cyan-500/40" />
        </div>
      </motion.header>

      {/* ── メイン ── */}
      <motion.main
        className="relative z-10 flex flex-col items-center flex-1 px-golden-md pt-4 pb-8"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.9, delay: 0.2 }}
      >

        {/* ══════════════════════════════════════════
            🎖️ ランク別ウェルカムバナー（ログイン済み・visitCount確定後）
        ══════════════════════════════════════════ */}
        {user && visitCount !== null && (() => {
          const rank = getRank(visitCount);
          const cfg = RANK_CONFIG[rank];
          if (rank === 'first') {
            return (
              <motion.div
                className="w-full max-w-sm mb-golden-md"
                initial={{ opacity: 0, y: -16, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.05 }}
              >
                <div
                  className="rounded-golden-lg p-px"
                  style={{
                    background: 'linear-gradient(135deg, rgba(74,222,128,0.7), rgba(16,185,129,0.4))',
                    boxShadow: '0 0 30px rgba(74,222,128,0.2)',
                  }}
                >
                  <div className="bg-black/85 rounded-golden-lg px-golden-md py-golden-sm flex items-center gap-golden-sm backdrop-blur-sm">
                    <motion.div
                      className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: 'rgba(74,222,128,0.15)', border: '2px solid rgba(74,222,128,0.5)' }}
                      animate={{ boxShadow: ['0 0 10px rgba(74,222,128,0.3)', '0 0 25px rgba(74,222,128,0.6)', '0 0 10px rgba(74,222,128,0.3)'] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <i className="ri-seedling-line text-2xl" style={{ color: '#4ade80' }} />
                    </motion.div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold tracking-[0.35em] mb-0.5" style={{ color: 'rgba(74,222,128,0.7)' }}>
                        FIRST VISIT
                      </p>
                      <p className="text-white font-black text-base tracking-wide leading-tight">
                        はじめまして！🌱
                      </p>
                      <p className="text-white/50 text-[11px] mt-0.5 leading-snug">
                        {shopData?.shop_name} への初来場へようこそ
                      </p>
                    </div>
                    <motion.div
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: '#4ade80' }}
                      animate={{ opacity: [1, 0.2, 1] }}
                      transition={{ duration: 1.4, repeat: Infinity }}
                    />
                  </div>
                </div>
              </motion.div>
            );
          }
          if (rank === 'regular') {
            return (
              <motion.div
                className="w-full max-w-sm mb-golden-md"
                initial={{ opacity: 0, y: -16, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                transition={{ duration: 0.7, delay: 0.05 }}
              >
                <div
                  className="rounded-golden-lg p-px"
                  style={{
                    background: 'linear-gradient(135deg, rgba(251,146,60,0.7), rgba(234,88,12,0.4))',
                    boxShadow: '0 0 30px rgba(251,146,60,0.2)',
                  }}
                >
                  <div className="bg-black/85 rounded-golden-lg px-golden-md py-golden-sm flex items-center gap-golden-sm backdrop-blur-sm">
                    <motion.div
                      className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                      style={{ background: 'rgba(251,146,60,0.15)', border: '2px solid rgba(251,146,60,0.5)' }}
                      animate={{ boxShadow: ['0 0 10px rgba(251,146,60,0.3)', '0 0 25px rgba(251,146,60,0.6)', '0 0 10px rgba(251,146,60,0.3)'] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <motion.i
                        className="ri-fire-line text-2xl"
                        style={{ color: '#fb923c' }}
                        animate={{ scale: [1, 1.15, 1] }}
                        transition={{ duration: 1.2, repeat: Infinity }}
                      />
                    </motion.div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[10px] font-bold tracking-[0.35em] mb-0.5" style={{ color: 'rgba(251,146,60,0.7)' }}>
                        REGULAR — {visitCount}回来場
                      </p>
                      <p className="text-white font-black text-base tracking-wide leading-tight">
                        おかえり、常連さん！🔥
                      </p>
                      <p className="text-white/50 text-[11px] mt-0.5 leading-snug">
                        いつもありがとう。今日も最高の夜を
                      </p>
                    </div>
                    <motion.div
                      className="w-2 h-2 rounded-full shrink-0"
                      style={{ background: '#fb923c' }}
                      animate={{ opacity: [1, 0.2, 1] }}
                      transition={{ duration: 1.4, repeat: Infinity }}
                    />
                  </div>
                </div>
              </motion.div>
            );
          }
          // deep_diver
          return (
            <motion.div
              className="w-full max-w-sm mb-golden-md"
              initial={{ opacity: 0, y: -16, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.7, delay: 0.05 }}
            >
              <div
                className="rounded-golden-lg p-px relative overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, #22d3ee, #a855f7, #22d3ee)',
                  backgroundSize: '200% auto',
                  boxShadow: '0 0 40px rgba(34,211,238,0.35), 0 0 80px rgba(168,85,247,0.2)',
                }}
              >
                {/* 流れるボーダー */}
                <motion.div
                  className="absolute inset-0 rounded-golden-lg"
                  style={{ background: 'linear-gradient(135deg, #22d3ee, #a855f7, #22d3ee)', backgroundSize: '300% auto' }}
                  animate={{ backgroundPosition: ['0% 50%', '100% 50%', '0% 50%'] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'linear' }}
                />
                <div className="relative bg-black/85 rounded-golden-lg px-golden-md py-golden-sm flex items-center gap-golden-sm backdrop-blur-sm">
                  <motion.div
                    className="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: 'rgba(34,211,238,0.15)', border: '2px solid rgba(34,211,238,0.6)' }}
                    animate={{ boxShadow: ['0 0 15px rgba(34,211,238,0.4)', '0 0 35px rgba(34,211,238,0.8)', '0 0 15px rgba(34,211,238,0.4)'] }}
                    transition={{ duration: 1.8, repeat: Infinity }}
                  >
                    <motion.i
                      className="ri-vip-crown-fill text-2xl text-cyan-400"
                      animate={{ rotate: [0, -8, 8, -5, 5, 0] }}
                      transition={{ duration: 3, repeat: Infinity, repeatDelay: 2 }}
                    />
                  </motion.div>
                  <div className="flex-1 min-w-0">
                    <p className="text-[10px] font-bold tracking-[0.35em] mb-0.5 text-cyan-400/70">
                      DEEP DIVER — {visitCount}回来場
                    </p>
                    <motion.p
                      className="font-black text-base tracking-wide leading-tight"
                      style={{
                        background: 'linear-gradient(90deg, #22d3ee, #a855f7)',
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                      }}
                    >
                      深淵の探求者よ、再び 👑
                    </motion.p>
                    <p className="text-white/50 text-[11px] mt-0.5 leading-snug">
                      この空間の最深部を知る者
                    </p>
                  </div>
                  <motion.div
                    className="flex flex-col gap-1 shrink-0"
                    animate={{ opacity: [0.5, 1, 0.5] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    {[0, 1, 2].map((i) => (
                      <motion.div
                        key={i}
                        className="w-1.5 h-1.5 rounded-full bg-cyan-400"
                        animate={{ opacity: [1, 0.2, 1] }}
                        transition={{ duration: 1.2, repeat: Infinity, delay: i * 0.3 }}
                      />
                    ))}
                  </motion.div>
                </div>
              </div>
            </motion.div>
          );
        })()}

        {/* ══════════════════════════════════════════
            🎨 演出パターン セクション
        ══════════════════════════════════════════ */}
        {patternConfig && (
          <motion.div
            className="w-full max-w-sm mb-golden-md"
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.15 }}
          >
            {/* ── type='text': テキストメッセージ演出 ── */}
            {patternConfig.type === 'text' && patternConfig.config?.message && (
              <div
                className="rounded-golden-lg p-px"
                style={{
                  background: 'linear-gradient(135deg, rgba(34,211,238,0.5), rgba(168,85,247,0.5))',
                  boxShadow: '0 0 40px rgba(34,211,238,0.2), 0 0 80px rgba(168,85,247,0.1)',
                }}
              >
                <div className="bg-black/85 rounded-golden-lg px-golden-md py-golden-md backdrop-blur-sm">
                  {/* ヘッダー */}
                  <div className="flex items-center gap-golden-xs mb-golden-sm">
                    <motion.div
                      className="w-7 h-7 flex items-center justify-center rounded-golden-sm"
                      style={{ background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.3)' }}
                      animate={{ boxShadow: ['0 0 8px rgba(34,211,238,0.3)', '0 0 20px rgba(34,211,238,0.6)', '0 0 8px rgba(34,211,238,0.3)'] }}
                      transition={{ duration: 2.5, repeat: Infinity }}
                    >
                      <i className="ri-message-3-line text-cyan-400 text-sm" />
                    </motion.div>
                    <div>
                      <p className="text-cyan-400 text-[10px] font-bold tracking-[0.3em]">MESSAGE FROM NODE</p>
                    </div>
                    <motion.div
                      className="ml-auto w-2 h-2 rounded-full bg-cyan-400"
                      animate={{ opacity: [1, 0.2, 1] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                    />
                  </div>

                  {/* メッセージ本文 */}
                  <div
                    className="rounded-golden-md px-golden-sm py-golden-sm"
                    style={{ background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.15)' }}
                  >
                    <p className="text-white/90 text-golden-base leading-relaxed tracking-wide whitespace-pre-line">
                      {patternConfig.config.message}
                    </p>
                  </div>

                  {/* デコレーション */}
                  <div className="flex items-center gap-golden-xs mt-golden-sm">
                    <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/20 to-transparent" />
                    <span className="text-cyan-400/30 text-[9px] tracking-[0.3em]">NODE EXCLUSIVE</span>
                    <div className="flex-1 h-px bg-gradient-to-l from-cyan-500/20 to-transparent" />
                  </div>
                </div>
              </div>
            )}

            {/* ── type='image': シークレット画像演出 ── */}
            {patternConfig.type === 'image' && patternConfig.config?.url && (
              <div
                className="rounded-golden-lg p-px overflow-hidden"
                style={{
                  background: 'linear-gradient(135deg, rgba(168,85,247,0.5), rgba(34,211,238,0.5))',
                  boxShadow: '0 0 40px rgba(168,85,247,0.2), 0 0 80px rgba(34,211,238,0.1)',
                }}
              >
                <div className="bg-black/85 rounded-golden-lg overflow-hidden backdrop-blur-sm">
                  {/* ヘッダー */}
                  <div
                    className="flex items-center gap-golden-xs px-golden-sm py-golden-sm border-b"
                    style={{ borderColor: 'rgba(168,85,247,0.2)', background: 'rgba(168,85,247,0.08)' }}
                  >
                    <motion.div
                      className="w-7 h-7 flex items-center justify-center rounded-golden-sm"
                      style={{ background: 'rgba(168,85,247,0.15)', border: '1px solid rgba(168,85,247,0.3)' }}
                      animate={{ boxShadow: ['0 0 8px rgba(168,85,247,0.3)', '0 0 20px rgba(168,85,247,0.6)', '0 0 8px rgba(168,85,247,0.3)'] }}
                      transition={{ duration: 2.5, repeat: Infinity }}
                    >
                      <i className="ri-image-line text-purple-400 text-sm" />
                    </motion.div>
                    <div>
                      <p className="text-purple-400 text-[10px] font-bold tracking-[0.3em]">SECRET VISUAL</p>
                    </div>
                    <motion.div
                      className="ml-auto flex items-center gap-1.5"
                    >
                      <motion.div
                        className="w-1.5 h-1.5 rounded-full bg-purple-400"
                        animate={{ opacity: [1, 0.2, 1] }}
                        transition={{ duration: 1.5, repeat: Infinity }}
                      />
                      <span className="text-purple-400/60 text-[9px] tracking-wider">LIVE</span>
                    </motion.div>
                  </div>

                  {/* 画像 */}
                  <motion.div
                    className="relative w-full aspect-video overflow-hidden"
                    whileHover={{ scale: 1.02 }}
                    transition={{ duration: 0.4 }}
                  >
                    <img
                      src={patternConfig.config.url}
                      alt="Secret Visual"
                      className="w-full h-full object-cover"
                    />
                    {/* グラデーションオーバーレイ */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-transparent to-transparent" />
                    <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-transparent to-transparent" />

                    {/* スキャンライン演出 */}
                    <motion.div
                      className="absolute inset-0 pointer-events-none"
                      style={{
                        background: 'repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(168,85,247,0.03) 2px, rgba(168,85,247,0.03) 4px)',
                      }}
                    />

                    {/* ボトムラベル */}
                    <div className="absolute bottom-3 left-4 right-4 flex items-center justify-between">
                      <div className="flex items-center gap-golden-xs">
                        <motion.div
                          className="w-2 h-2 rounded-full bg-purple-400"
                          animate={{ opacity: [1, 0.3, 1] }}
                          transition={{ duration: 1.2, repeat: Infinity }}
                        />
                        <span className="text-purple-300 text-[10px] font-bold tracking-[0.3em]">
                          NODE EXCLUSIVE
                        </span>
                      </div>
                      <span className="text-white/40 text-[9px] tracking-wider">
                        {shopData?.shop_name}
                      </span>
                    </div>
                  </motion.div>

                  {/* フッター */}
                  <div className="px-golden-sm py-golden-sm flex items-center justify-center gap-golden-xs">
                    <i className="ri-lock-unlock-line text-purple-400/60 text-xs" />
                    <span className="text-purple-400/60 text-[10px] tracking-wider">
                      NFCタップで解放されたコンテンツ
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* ── type='coupon': デジタルクーポン演出 ── */}
            {patternConfig.type === 'coupon' && patternConfig.config?.discount && (
              <div
                className="rounded-golden-lg p-px"
                style={{
                  background: 'linear-gradient(135deg, rgba(251,191,36,0.6), rgba(249,115,22,0.5))',
                  boxShadow: '0 0 40px rgba(251,191,36,0.25), 0 0 80px rgba(249,115,22,0.1)',
                }}
              >
                <div className="bg-black/85 rounded-golden-lg overflow-hidden backdrop-blur-sm">
                  {/* ヘッダー */}
                  <div
                    className="flex items-center gap-golden-xs px-golden-sm py-golden-sm border-b"
                    style={{ borderColor: 'rgba(251,191,36,0.2)', background: 'rgba(251,191,36,0.08)' }}
                  >
                    <motion.div
                      className="w-7 h-7 flex items-center justify-center rounded-golden-sm"
                      style={{ background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.4)' }}
                      animate={{ boxShadow: ['0 0 8px rgba(251,191,36,0.3)', '0 0 20px rgba(251,191,36,0.6)', '0 0 8px rgba(251,191,36,0.3)'] }}
                      transition={{ duration: 2, repeat: Infinity }}
                    >
                      <i className="ri-coupon-line text-amber-400 text-sm" />
                    </motion.div>
                    <div>
                      <p className="text-amber-400 text-[10px] font-bold tracking-[0.3em]">SPECIAL COUPON</p>
                    </div>
                    <motion.div
                      className="ml-auto"
                      animate={{ rotate: [0, -10, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    >
                      <span className="text-lg">🎟️</span>
                    </motion.div>
                  </div>

                  {/* クーポン本体 */}
                  <div className="px-golden-md py-golden-lg">
                    {/* ミシン目風デザイン */}
                    <div className="flex items-center gap-1 mb-golden-sm">
                      {Array.from({ length: 20 }).map((_, i) => (
                        <div key={i} className="flex-1 h-px" style={{ background: 'rgba(251,191,36,0.2)' }} />
                      ))}
                    </div>

                    {/* 割引テキスト */}
                    <div className="text-center mb-golden-sm">
                      <p className="text-amber-400/60 text-[10px] tracking-[0.4em] mb-golden-xs">DISCOUNT</p>
                      <motion.p
                        className="text-4xl font-black tracking-wider"
                        style={{
                          background: 'linear-gradient(135deg, #fbbf24, #f97316)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          textShadow: 'none',
                          filter: 'drop-shadow(0 0 20px rgba(251,191,36,0.5))',
                        }}
                        animate={{ filter: ['drop-shadow(0 0 10px rgba(251,191,36,0.4))', 'drop-shadow(0 0 25px rgba(251,191,36,0.7))', 'drop-shadow(0 0 10px rgba(251,191,36,0.4))'] }}
                        transition={{ duration: 2.5, repeat: Infinity }}
                      >
                        {patternConfig.config.discount}
                      </motion.p>
                    </div>

                    {/* ミシン目風デザイン */}
                    <div className="flex items-center gap-1 mb-golden-sm">
                      {Array.from({ length: 20 }).map((_, i) => (
                        <div key={i} className="flex-1 h-px" style={{ background: 'rgba(251,191,36,0.2)' }} />
                      ))}
                    </div>

                    <p className="text-white/50 text-xs text-center tracking-wide mb-golden-md">
                      このクーポンをスタッフに提示してください
                    </p>

                    {/* コピーボタン */}
                    <motion.button
                      className="w-full flex items-center justify-center gap-golden-xs py-golden-sm rounded-golden-md cursor-pointer whitespace-nowrap"
                      style={{
                        background: copiedCoupon
                          ? 'rgba(34,211,238,0.15)'
                          : 'linear-gradient(135deg, rgba(251,191,36,0.2), rgba(249,115,22,0.15))',
                        border: `1px solid ${copiedCoupon ? 'rgba(34,211,238,0.5)' : 'rgba(251,191,36,0.4)'}`,
                        boxShadow: copiedCoupon ? '0 0 20px rgba(34,211,238,0.2)' : '0 0 20px rgba(251,191,36,0.15)',
                      }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => handleCopyCoupon(patternConfig.config.discount)}
                    >
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className="ri-check-double-line text-cyan-400" />
                      </div>
                      <span className={`text-xs font-bold tracking-widest ${copiedCoupon ? 'text-cyan-400' : 'text-amber-400'}`}>
                        {copiedCoupon ? 'COPIED! ✓' : 'クーポンコードをコピー'}
                      </span>
                    </motion.button>
                  </div>

                  {/* フッター */}
                  <div
                    className="px-golden-sm py-golden-xs flex items-center justify-center gap-golden-xs border-t"
                    style={{ borderColor: 'rgba(251,191,36,0.15)', background: 'rgba(251,191,36,0.04)' }}
                  >
                    <i className="ri-nfc-line text-amber-400/40 text-xs" />
                    <span className="text-amber-400/40 text-[9px] tracking-wider">
                      NFCタップで取得した限定クーポン
                    </span>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {/* ══════════════════════════════════════════
            🌟 デフォルト演出（パターン未設定時）
        ══════════════════════════════════════════ */}
        {!patternConfig && (
          <DefaultWelcomeCard
            shopName={shopData.shop_name}
            welcomeMessage={profileData?.nfc_welcome_message || ''}
            visitCount={user && visitCount !== null ? visitCount : null}
          />
        )}

        {/* NOW PLAYING カード */}
        {nowPlaying && (
          <div
            className="w-full max-w-sm rounded-golden-lg p-px"
            style={{
              background: 'linear-gradient(135deg, rgba(34,211,238,0.4), rgba(168,85,247,0.4))',
            }}
          >
            <div className="bg-black/80 rounded-golden-lg p-golden-lg flex flex-col items-center gap-golden-sm backdrop-blur-sm">
              {/* ラベル */}
              <div className="flex items-center gap-golden-xs">
                <motion.div
                  className="w-2 h-2 rounded-full bg-cyan-400"
                  animate={{ opacity: [1, 0.2, 1] }}
                  transition={{ duration: 1.2, repeat: Infinity }}
                />
                <span className="text-cyan-400 text-[10px] font-bold tracking-[0.4em]">
                  NOW PLAYING
                </span>
              </div>

              {/* アルバムアート風ビジュアル */}
              <div className="relative w-36 h-36">
                <motion.div
                  className="absolute inset-0 rounded-full"
                  style={{
                    background: 'conic-gradient(from 0deg, #22d3ee, #a855f7, #22d3ee)',
                    opacity: 0.3,
                  }}
                  animate={{ rotate: 360 }}
                  transition={{ duration: 8, repeat: Infinity, ease: 'linear' }}
                />
                <div
                  className="absolute inset-2 rounded-full bg-black/90 flex items-center justify-center"
                  style={{ boxShadow: '0 0 30px rgba(34,211,238,0.3)' }}
                >
                  <motion.div
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <i className="ri-music-2-line text-4xl text-cyan-400/80" />
                  </motion.div>
                </div>
              </div>

              {/* 曲名・アーティスト名 */}
              <div className="text-center">
                <p className="text-white text-lg font-bold tracking-wide leading-tight">
                  {nowPlaying.title}
                </p>
                <p className="text-cyan-400/70 text-sm tracking-widest mt-1">
                  {nowPlaying.artist}
                </p>
                <p className="text-white/30 text-xs mt-1">{nowPlaying.duration}</p>
              </div>

              {/* プログレスバー */}
              <div className="w-full">
                <div className="w-full h-0.5 bg-white/10 rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{
                      background: 'linear-gradient(90deg, #22d3ee, #a855f7)',
                      width: '42%',
                    }}
                    animate={{ opacity: [0.7, 1, 0.7] }}
                    transition={{ duration: 2, repeat: Infinity }}
                  />
                </div>
                <div className="flex justify-between mt-1">
                  <span className="text-white/30 text-[10px]">1:54</span>
                  <span className="text-white/30 text-[10px]">{nowPlaying.duration}</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* RESONATE ボタン */}
        <motion.button
          className="mt-golden-lg relative px-golden-xl py-golden-sm rounded-full font-black tracking-[0.3em] text-sm text-black cursor-pointer whitespace-nowrap overflow-hidden"
          style={{
            background: 'linear-gradient(135deg, #22d3ee, #a855f7)',
            boxShadow: '0 0 30px rgba(34,211,238,0.4), 0 0 60px rgba(168,85,247,0.2)',
          }}
          whileHover={{ scale: 1.05, boxShadow: '0 0 50px rgba(34,211,238,0.6), 0 0 80px rgba(168,85,247,0.4)' }}
          whileTap={{ scale: 0.97 }}
          onClick={() => navigate(`/shop/${shopData.id}`)}
        >
          <motion.div
            className="absolute inset-0 bg-white/20"
            animate={{ x: ['-100%', '200%'] }}
            transition={{ duration: 2.5, repeat: Infinity, ease: 'easeInOut' }}
            style={{ skewX: '-20deg' }}
          />
          <span className="relative z-10 flex items-center gap-golden-xs">
            <i className="ri-radio-line text-base" />
            RESONATE
          </span>
        </motion.button>

        <p className="mt-2 text-white/30 text-[10px] tracking-widest">
          このアーティストと共鳴する
        </p>

        {/* ── チェックインランクバッジ（ログインユーザーのみ） ── */}
        {user && visitCount !== null && (
          <div className="w-full max-w-sm mt-golden-xl">
            <CheckInRankBadge visitCount={visitCount} shopName={shopData.shop_name} />
          </div>
        )}

        {/* ── 未ログイン時：ランク取得を促すメッセージ ── */}
        {!user && (
          <motion.div
            className="w-full max-w-sm mt-golden-xl"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
          >
            <div className="flex items-center gap-golden-sm mb-golden-sm">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/50 text-[10px] font-bold tracking-[0.4em]">
                YOUR RANK
              </span>
              <div className="flex-1 h-px bg-white/10" />
            </div>
            <div
              className="rounded-golden-lg p-px"
              style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.1), rgba(255,255,255,0.05))' }}
            >
              <div className="bg-black/70 rounded-golden-lg p-golden-lg backdrop-blur-sm text-center">
                <i className="ri-lock-line text-3xl text-white/20" />
                <p className="text-white/40 text-sm">ログインすると来場回数に応じた<br />ランクが表示されます</p>
                <motion.button
                  className="mt-1 px-golden-md py-golden-xs rounded-full text-xs font-bold tracking-widest text-black cursor-pointer whitespace-nowrap"
                  style={{ background: 'linear-gradient(135deg, #22d3ee, #a855f7)' }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.97 }}
                  onClick={() => navigate('/login')}
                >
                  LOGIN
                </motion.button>
              </div>
            </div>
          </motion.div>
        )}

        {/* ── 表示モード別コンテンツ ── */}
        {displayMode === 'event' ? (
          // ─── イベントモード: TODAY'S EVENT ───
          <motion.section
            className="w-full max-w-sm mt-golden-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="flex items-center gap-golden-sm mb-golden-sm">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/50 text-[10px] font-bold tracking-[0.4em]">
                TODAY'S EVENT
              </span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {events.length > 0 ? (
              <div className="flex flex-col gap-golden-sm">
                {events.map((event, i) => (
                  <motion.div
                    key={event.id}
                    className="rounded-golden-md p-px cursor-pointer"
                    style={{
                      background: 'linear-gradient(135deg, rgba(34,211,238,0.2), rgba(168,85,247,0.2))',
                    }}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 + i * 0.15 }}
                    whileHover={{ scale: 1.02 }}
                    onClick={() => navigate(`/event/${event.id}`)}
                  >
                    <div className="bg-black/70 rounded-golden-md px-golden-sm py-golden-sm backdrop-blur-sm">
                      <div className="flex items-start justify-between gap-golden-sm">
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm font-bold tracking-wide leading-tight truncate">
                            {event.title}
                          </p>
                          {event.category && (
                            <p className="text-white/40 text-[10px] tracking-wider mt-0.5">
                              {event.category}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          <i className="ri-time-line text-cyan-400/60 text-xs w-3 h-3 flex items-center justify-center" />
                          <span className="text-cyan-400/70 text-[10px] tracking-wide whitespace-nowrap">
                            {event.time || '時間未定'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8">
                <i className="ri-calendar-line text-3xl text-white/20 mb-2" />
                <p className="text-white/40 text-sm">本日のイベントはありません</p>
              </div>
            )}
          </motion.section>
        ) : (
          // ─── 店舗モード: SHOP INFO ───
          <motion.section
            className="w-full max-w-sm mt-golden-xl"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.5 }}
          >
            <div className="flex items-center gap-golden-sm mb-golden-sm">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-white/50 text-[10px] font-bold tracking-[0.4em]">
                SHOP INFO
              </span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            <div
              className="rounded-golden-md p-px"
              style={{
                background: 'linear-gradient(135deg, rgba(34,211,238,0.2), rgba(168,85,247,0.2))',
              }}
            >
              <div className="bg-black/70 rounded-golden-md p-golden-md backdrop-blur-sm space-y-golden-sm">
                {shopData.description && (
                  <div>
                    <p className="text-white/90 text-golden-base leading-relaxed">
                      {shopData.description}
                    </p>
                  </div>
                )}

                {shopData.address && (
                  <div className="flex items-start gap-golden-sm">
                    <i className="ri-map-pin-line text-cyan-400/60 text-base mt-0.5 w-4 h-4 flex items-center justify-center" />
                    <div className="flex-1">
                      <p className="text-white/40 text-[10px] tracking-wider mb-1">ADDRESS</p>
                      <p className="text-white/80 text-xs leading-relaxed">{shopData.address}</p>
                    </div>
                  </div>
                )}

                {shopData.phone && (
                  <div className="flex items-start gap-golden-sm">
                    <i className="ri-phone-line text-cyan-400/60 text-base mt-0.5 w-4 h-4 flex items-center justify-center" />
                    <div className="flex-1">
                      <p className="text-white/40 text-[10px] tracking-wider mb-1">PHONE</p>
                      <p className="text-white/80 text-xs">{shopData.phone}</p>
                    </div>
                  </div>
                )}

                {shopData.business_hours && (
                  <div className="flex items-start gap-golden-sm">
                    <i className="ri-time-line text-cyan-400/60 text-base mt-0.5 w-4 h-4 flex items-center justify-center" />
                    <div className="flex-1">
                      <p className="text-white/40 text-[10px] tracking-wider mb-1">HOURS</p>
                      <p className="text-white/80 text-xs whitespace-pre-line">{shopData.business_hours}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </motion.section>
        )}

        {/* ── SNSシェアセクション ── */}
        <motion.section
          className="w-full max-w-sm mt-golden-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.7 }}
        >
          <div className="flex items-center gap-golden-sm mb-golden-sm">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/50 text-[10px] font-bold tracking-[0.4em]">
              SHARE YOUR VISIT
            </span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          <div
            className="rounded-golden-lg p-px"
            style={{ background: 'linear-gradient(135deg, rgba(34,211,238,0.2), rgba(168,85,247,0.2))' }}
          >
            <div className="bg-black/70 rounded-golden-lg p-golden-md backdrop-blur-sm">
              <p className="text-white/50 text-xs text-center leading-relaxed tracking-wide mb-golden-md">
                来場をシェアして、あなたのDIGNIA体験を広めよう 🎶
              </p>

              <div className="flex flex-col gap-golden-sm">
                {/* X (Twitter) */}
                <motion.button
                  className="flex items-center gap-golden-sm px-golden-sm py-golden-sm rounded-golden-md cursor-pointer whitespace-nowrap w-full text-left"
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.12)',
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleXShare}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-black border border-white/20 shrink-0">
                    <i className="ri-twitter-x-line text-white w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-bold tracking-wide">X (Twitter)</p>
                    <p className="text-white/40 text-[10px] tracking-wider">友だちにシェアする</p>
                  </div>
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-external-link-line text-white/30 text-xs" />
                  </div>
                </motion.button>

                {/* LINE */}
                <motion.button
                  className="flex items-center gap-golden-sm px-golden-sm py-golden-sm rounded-golden-md cursor-pointer whitespace-nowrap w-full text-left"
                  style={{
                    background: 'rgba(0,195,0,0.08)',
                    border: '1px solid rgba(0,195,0,0.2)',
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleLineShare}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full shrink-0" style={{ background: '#06C755' }}>
                    <i className="ri-line-line text-white w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-bold tracking-wide">LINE</p>
                    <p className="text-white/40 text-[10px] tracking-wider">友だちにシェアする</p>
                  </div>
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-external-link-line text-white/30 text-xs" />
                  </div>
                </motion.button>

                {/* Instagram (copy link) */}
                <motion.button
                  className="flex items-center gap-golden-sm px-golden-sm py-golden-sm rounded-golden-md cursor-pointer whitespace-nowrap w-full text-left"
                  style={{
                    background: 'rgba(193,53,132,0.08)',
                    border: '1px solid rgba(193,53,132,0.2)',
                  }}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={handleInstagramShare}
                >
                  <div className="w-8 h-8 flex items-center justify-center rounded-full shrink-0" style={{ background: '#C13584' }}>
                    <i className="ri-instagram-line text-white w-4 h-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white text-sm font-bold tracking-wide">Instagram (Copy Link)</p>
                    <p className="text-white/40 text-[10px] tracking-wider">リンクをコピー</p>
                  </div>
                  <div className="w-4 h-4 flex items-center justify-center shrink-0">
                    <i className="ri-clipboard-line text-white/30 text-xs" />
                  </div>
                </motion.button>
              </div>
            </div>
          </div>
        </motion.section>

      </motion.main>

      {/* ── クーポン提示用フルスクリーンモーダル ── */}
      {showCouponStaffView && deepDiverCoupon && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-md px-golden-md"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          onClick={() => setShowCouponStaffView(false)}
        >
          <motion.div
            className="w-full max-w-md"
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ delay: 0.1 }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* 閉じるボタン */}
            <div className="flex justify-end mb-golden-sm">
              <motion.button
                className="w-10 h-10 flex items-center justify-center rounded-full cursor-pointer"
                style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)' }}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowCouponStaffView(false)}
              >
                <i className="ri-close-line text-white text-xl" />
              </motion.button>
            </div>

            {/* クーポン大表示 */}
            <div
              className="rounded-golden-xl p-1"
              style={{
                background: 'linear-gradient(135deg, #fbbf24, #f97316)',
                boxShadow: '0 0 60px rgba(251,191,36,0.5)',
              }}
            >
              <div className="bg-black rounded-golden-xl px-golden-2xl py-golden-3xl">
                <div className="text-center mb-golden-2xl">
                  <motion.div
                    className="inline-block mb-golden-sm"
                    animate={{ scale: [1, 1.1, 1] }}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <span className="text-6xl">🎟️</span>
                  </motion.div>
                  <p className="text-amber-400 text-xs tracking-[0.4em] mb-golden-sm">DEEP DIVER EXCLUSIVE</p>
                  <motion.p
                    className="text-7xl font-black tracking-wider mb-golden-lg"
                    style={{
                      background: 'linear-gradient(135deg, #fbbf24, #f97316)',
                      WebkitBackgroundClip: 'text',
                      WebkitTextFillColor: 'transparent',
                      filter: 'drop-shadow(0 0 30px rgba(251,191,36,0.6))',
                    }}
                    animate={{
                      filter: [
                        'drop-shadow(0 0 20px rgba(251,191,36,0.5))',
                        'drop-shadow(0 0 40px rgba(251,191,36,0.8))',
                        'drop-shadow(0 0 20px rgba(251,191,36,0.5))',
                      ],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                  >
                    {deepDiverCoupon.discount}
                  </motion.p>
                  <div className="flex items-center justify-center gap-golden-xs mb-golden-lg">
                    <div className="flex-1 h-px bg-amber-400/20" />
                    <span className="text-amber-400/60 text-xs tracking-wider">COUPON</span>
                    <div className="flex-1 h-px bg-amber-400/20" />
                  </div>
                  <p className="text-white/70 text-sm tracking-wide mb-golden-sm">
                    このクーポンをスタッフに提示してください
                  </p>
                  <CountdownTimer expiresAt={deepDiverCoupon.expires_at} large />
                </div>

                <motion.div
                  className="flex items-center justify-center gap-golden-xs py-golden-sm rounded-golden-lg"
                  style={{ background: 'rgba(251,191,36,0.1)', border: '1px solid rgba(251,191,36,0.3)' }}
                  animate={{ opacity: [0.7, 1, 0.7] }}
                  transition={{ duration: 2, repeat: Infinity }}
                >
                  <i className="ri-user-star-line text-amber-400 text-lg" />
                  <span className="text-amber-400 text-sm font-bold tracking-widest">
                    スタッフ確認用画面
                  </span>
                </motion.div>
              </div>
            </div>

            <p className="text-white/40 text-xs text-center mt-golden-sm tracking-wide">
              画面をタップして閉じる
            </p>
          </motion.div>
        </motion.div>
      )}
    </div>
  );
}

// ── デフォルトウェルカムカードコンポーネント ──
function DefaultWelcomeCard({
  shopName,
  welcomeMessage,
  visitCount,
}: {
  shopName: string;
  welcomeMessage: string;
  visitCount: number | null;
}) {
  return (
    <motion.div
      className="w-full max-w-sm mb-golden-md"
      initial={{ opacity: 0, y: 20, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      transition={{ duration: 0.8, delay: 0.15 }}
    >
      <div
        className="rounded-golden-lg p-px"
        style={{
          background: 'linear-gradient(135deg, rgba(34,211,238,0.5), rgba(168,85,247,0.5))',
          boxShadow: '0 0 40px rgba(34,211,238,0.15), 0 0 80px rgba(168,85,247,0.1)',
        }}
      >
        <div className="bg-black/85 rounded-golden-lg px-golden-md py-golden-lg backdrop-blur-sm">
          {/* ヘッダー */}
          <div className="flex items-center gap-golden-xs mb-golden-sm">
            <motion.div
              className="w-7 h-7 flex items-center justify-center rounded-golden-sm"
              style={{ background: 'rgba(34,211,238,0.15)', border: '1px solid rgba(34,211,238,0.3)' }}
              animate={{ boxShadow: ['0 0 8px rgba(34,211,238,0.3)', '0 0 20px rgba(34,211,238,0.6)', '0 0 8px rgba(34,211,238,0.3)'] }}
              transition={{ duration: 2.5, repeat: Infinity }}
            >
              <i className="ri-store-2-line text-cyan-400 text-sm" />
            </motion.div>
            <p className="text-cyan-400 text-[10px] font-bold tracking-[0.3em]">NODE PORTAL</p>
            <motion.div
              className="ml-auto w-2 h-2 rounded-full bg-cyan-400"
              animate={{ opacity: [1, 0.2, 1] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            />
          </div>

          {/* ウェルカムメッセージ */}
          {welcomeMessage ? (
            <div
              className="rounded-golden-md px-golden-sm py-golden-sm mb-golden-sm"
              style={{ background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.15)' }}
            >
              <p className="text-white/90 text-golden-base leading-relaxed tracking-wide whitespace-pre-line">
                {welcomeMessage}
              </p>
            </div>
          ) : (
            <div
              className="rounded-golden-md px-golden-sm py-golden-sm mb-golden-sm"
              style={{ background: 'rgba(34,211,238,0.05)', border: '1px solid rgba(34,211,238,0.15)' }}
            >
              <p className="text-white/70 text-golden-base leading-relaxed tracking-wide text-center">
                🎵 {shopName} へようこそ！<br />
                <span className="text-white/40 text-xs">NFCタップでポータルが開きました</span>
              </p>
            </div>
          )}

          {/* 来場回数 */}
          {visitCount !== null && (
            <div className="flex items-center justify-center gap-golden-xs">
              <i className="ri-map-pin-2-line text-cyan-400/50 text-xs" />
              <span className="text-white/40 text-[10px] tracking-wider">
                通算来場回数：<span className="text-cyan-400 font-bold">{visitCount}</span> 回
              </span>
            </div>
          )}

          {/* デコレーション */}
          <div className="flex items-center gap-golden-xs mt-golden-sm">
            <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/20 to-transparent" />
            <span className="text-cyan-400/30 text-[9px] tracking-[0.3em]">NODE EXCLUSIVE</span>
            <div className="flex-1 h-px bg-gradient-to-l from-cyan-500/20 to-transparent" />
          </div>
        </div>
      </div>
    </motion.div>
  );
}

// ── カウントダウンタイマーコンポーネント ──
function CountdownTimer({ expiresAt, large = false }: { expiresAt: string; large?: boolean }) {
  const [timeLeft, setTimeLeft] = useState<{
    days: number;
    hours: number;
    minutes: number;
    seconds: number;
  } | null>(null);

  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const diff = expiry - now;

      if (diff <= 0) {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
        return;
      }

      const days = Math.floor(diff / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diff % (1000 * 60)) / 1000);

      setTimeLeft({ days, hours, minutes, seconds });
    };

    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);

    return () => clearInterval(timer);
  }, [expiresAt]);

  if (!timeLeft) return null;

  if (large) {
    return (
      <div className="flex items-center justify-center gap-3">
        {timeLeft.days > 0 && (
          <div className="text-center">
            <p className="text-3xl font-black text-amber-400">{timeLeft.days}</p>
            <p className="text-[10px] text-white/40 tracking-wider">日</p>
          </div>
        )}
        <div className="text-center">
          <p className="text-3xl font-black text-amber-400">{String(timeLeft.hours).padStart(2, '0')}</p>
          <p className="text-[10px] text-white/40 tracking-wider">時間</p>
        </div>
        <span className="text-2xl text-amber-400/40">:</span>
        <div className="text-center">
          <p className="text-3xl font-black text-amber-400">{String(timeLeft.minutes).padStart(2, '0')}</p>
          <p className="text-[10px] text-white/40 tracking-wider">分</p>
        </div>
        <span className="text-2xl text-amber-400/40">:</span>
        <div className="text-center">
          <p className="text-3xl font-black text-amber-400">{String(timeLeft.seconds).padStart(2, '0')}</p>
          <p className="text-[10px] text-white/40 tracking-wider">秒</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center gap-2">
      {timeLeft.days > 0 && (
        <>
          <div className="text-center">
            <p className="text-lg font-black text-amber-400">{timeLeft.days}</p>
            <p className="text-[9px] text-white/40 tracking-wider">日</p>
          </div>
          <span className="text-amber-400/40">:</span>
        </>
      )}
      <div className="text-center">
        <p className="text-lg font-black text-amber-400">{String(timeLeft.hours).padStart(2, '0')}</p>
        <p className="text-[9px] text-white/40 tracking-wider">時間</p>
      </div>
      <span className="text-amber-400/40">:</span>
      <div className="text-center">
        <p className="text-lg font-black text-amber-400">{String(timeLeft.minutes).padStart(2, '0')}</p>
        <p className="text-[9px] text-white/40 tracking-wider">分</p>
      </div>
    </div>
  );
}